import React from "react";
import { motion } from "framer-motion";
import { LineChart, Settings2, ClipboardCheck, FileBarChart, ShieldCheck } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/motion";
import { SunArcs } from "@/components/SunArcs";

const CARDS = [
  {
    n: "01",
    icon: LineChart,
    title: "Analisi & Revenue Management",
    text: "Studio dello storico e del mercato, segmentazione per periodi, pricing dinamico che segue la domanda e protegge il valore quando serve. Niente prezzi fissi per tutta la stagione.",
    span: "md:col-span-7",
    feature: true,
  },
  {
    n: "02",
    icon: Settings2,
    title: "Gestione & distribuzione",
    text: "Onboarding, distribuzione su OTA e canali diretti, channel manager, messaggistica ospiti e coordinamento operativo. La struttura gira, tu segui i risultati.",
    span: "md:col-span-5",
  },
  {
    n: "03",
    icon: ClipboardCheck,
    title: "Avvio & compliance",
    text: "Apertura della struttura dalla A alla Z: setup annunci, CIN e normativa, tassa di soggiorno, listini e regole casa. Ti mettiamo in regola e sul mercato.",
    span: "md:col-span-5",
  },
  {
    n: "04",
    icon: FileBarChart,
    title: "Reportistica trasparente",
    text: "Report chiari e leggibili: quanto hai incassato, come e perché. Sempre sotto controllo, senza dover chiedere.",
    span: "md:col-span-7",
  },
  {
    n: "05",
    icon: ShieldCheck,
    title: "Il controllo resta tuo",
    text: "Tutti gli account — OTA, gestionale, booking engine — restano intestati a te. Alba opera come delegato: se un domani vuoi proseguire da solo, hai già tutto in mano. È una scelta, non un caso.",
    span: "md:col-span-12",
    highlight: true,
  },
];

export const Metodo = () => (
  <section id="metodo" data-testid="section-metodo" className="relative bg-white py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-3xl">
        <motion.p variants={fadeUp} className="mb-5 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
          Il metodo Alba
        </motion.p>
        <motion.h2 variants={fadeUp} className="font-display text-4xl font-medium tracking-tight text-navy sm:text-5xl">
          Prima i dati. Poi le decisioni.
        </motion.h2>
        <motion.p variants={fadeUp} className="mt-7 text-base leading-relaxed text-text-muted sm:text-lg">
          Non improvvisiamo né le tariffe né la gestione. Analizziamo lo storico, il mercato locale e
          l'andamento delle prenotazioni, e costruiamo una strategia di prezzo e di gestione che si
          muove insieme alla domanda.
        </motion.p>
      </motion.div>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
        className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8"
      >
        {CARDS.map((c) => {
          const Icon = c.icon;
          const dark = c.highlight;
          return (
            <motion.article
              key={c.n}
              variants={fadeUp}
              data-testid={`metodo-card-${c.n}`}
              className={`group relative overflow-hidden rounded-2xl border p-8 transition-transform duration-500 hover:-translate-y-1.5 md:p-10 ${c.span} ${
                dark
                  ? "border-gold/25 bg-navy text-white"
                  : "border-navy/10 bg-cream text-navy-deep shadow-[0_8px_30px_rgba(31,58,95,0.05)]"
              }`}
            >
              {dark && (
                <SunArcs className="pointer-events-none absolute -bottom-16 -right-10 w-72" rings={5} opacity={0.22} />
              )}
              <div className="relative flex items-start justify-between">
                <span className={`flex h-12 w-12 items-center justify-center rounded-full ${dark ? "bg-gold text-navy-deep" : "bg-navy text-gold"}`}>
                  <Icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <span className={`font-display text-4xl font-light ${dark ? "text-gold/70" : "text-navy/15"}`}>
                  {c.n}
                </span>
              </div>
              <h3 className={`relative mt-8 font-display text-2xl font-medium ${dark ? "text-white" : "text-navy"}`}>
                {c.title}
              </h3>
              <p className={`relative mt-4 text-base leading-relaxed ${dark ? "text-white/75" : "text-text-muted"}`}>
                {c.text}
              </p>
            </motion.article>
          );
        })}
      </motion.div>
    </div>
  </section>
);
